import { PauseIcon, PlayIcon, XIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

/**
 * Reference audio attached to a video generation (Seedance 2.0 / MiniMax H3).
 * The decoded buffer is kept so the selected window can be trimmed client-side
 * without re-decoding, and the object URL powers playback preview.
 */
export interface ReferenceAudioValue {
  file: File;
  buffer: AudioBuffer;
  objectUrl: string;
  durationSec: number;
  /** Selected window (equals the whole file when no trim is needed). */
  startSec: number;
  endSec: number;
}

const ACCEPTED_AUDIO =
  'audio/*,.mp3,.wav,.m4a,.aac,.ogg,.oga,.flac,.webm,.mp4a';
const MAX_AUDIO_FILE_BYTES = 50 * 1024 * 1024;
const MIN_WINDOW_SEC = 1;

const formatTime = (sec: number): string => {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
};

/** Decode an audio file into a ReferenceAudioValue with a default window. */
export async function decodeReferenceAudioFile(
  file: File,
  maxSeconds: number,
): Promise<ReferenceAudioValue> {
  const arrayBuffer = await file.arrayBuffer();
  const AudioContextCtor =
    window.AudioContext || (window as any).webkitAudioContext;
  const ctx = new AudioContextCtor();
  try {
    const buffer = await ctx.decodeAudioData(arrayBuffer);
    const durationSec = buffer.duration;
    return {
      file,
      buffer,
      objectUrl: URL.createObjectURL(file),
      durationSec,
      startSec: 0,
      endSec: Math.min(durationSec, maxSeconds),
    };
  } finally {
    void ctx.close();
  }
}

/** Whether the selected window covers less than the full file. */
const isTrimmed = (value: ReferenceAudioValue): boolean =>
  value.startSec > 0.05 || value.endSec < value.durationSec - 0.05;

/**
 * Encode a slice of an AudioBuffer as a 16-bit PCM WAV blob.
 * `mono` downmixes to a single channel, halving the output size — worth it for
 * long windows (voice cloning), since uncompressed WAV grows fast: five minutes
 * of 44.1kHz stereo is ~53MB.
 */
function encodeWavSlice(
  buffer: AudioBuffer,
  startSec: number,
  endSec: number,
  mono = false,
): Blob {
  const sampleRate = buffer.sampleRate;
  const sourceChannels = Math.min(buffer.numberOfChannels, 2);
  const channels = mono ? 1 : sourceChannels;
  const startFrame = Math.max(0, Math.floor(startSec * sampleRate));
  const endFrame = Math.min(buffer.length, Math.ceil(endSec * sampleRate));
  const frameCount = Math.max(0, endFrame - startFrame);
  const dataLength = frameCount * channels * 2;
  const arrayBuffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);

  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < sourceChannels; ch++) {
    channelData.push(buffer.getChannelData(ch));
  }
  const writeSample = (offset: number, value: number) => {
    const sample = Math.max(-1, Math.min(1, value));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  };

  let offset = 44;
  for (let frame = startFrame; frame < endFrame; frame++) {
    if (mono) {
      // Average the channels rather than dropping one, so content panned to a
      // single side isn't lost.
      let sum = 0;
      for (let ch = 0; ch < sourceChannels; ch++) sum += channelData[ch][frame];
      writeSample(offset, sum / sourceChannels);
      offset += 2;
    } else {
      for (let ch = 0; ch < channels; ch++) {
        writeSample(offset, channelData[ch][frame]);
        offset += 2;
      }
    }
  }
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/**
 * The bytes to send for the selected window. Untrimmed files are returned
 * as-is (keeps their compression); a trimmed window is encoded to WAV
 * client-side. Shared by the R2 upload below and the voice-cloning flow, which
 * uploads to Supabase storage instead.
 */
export function getReferenceAudioBlob(
  value: ReferenceAudioValue,
  options: { mono?: boolean } = {},
): {
  blob: Blob;
  fileName: string;
  contentType: string;
  trimmed: boolean;
} {
  const trimmed = isTrimmed(value);
  const blob: Blob = trimmed
    ? encodeWavSlice(value.buffer, value.startSec, value.endSec, options.mono)
    : value.file;
  const baseName = value.file.name.replace(/\.[^.]+$/, '') || 'reference';
  return {
    blob,
    fileName: trimmed ? `${baseName}-clip.wav` : value.file.name,
    contentType: trimmed ? 'audio/wav' : value.file.type || 'audio/mpeg',
    trimmed,
  };
}

/**
 * Upload the selected audio window to R2 and return its public URL.
 */
export async function uploadReferenceAudio(
  value: ReferenceAudioValue,
): Promise<string> {
  const { blob, fileName, contentType } = getReferenceAudioBlob(value);

  const presignRes = await fetch('/api/reference-audio-upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, contentType }),
  });
  if (!presignRes.ok) {
    throw new Error(`Audio upload failed (${presignRes.status})`);
  }
  const { uploadUrl, publicUrl } = (await presignRes.json()) as {
    uploadUrl: string;
    publicUrl: string;
  };
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: blob,
  });
  if (!putRes.ok) {
    throw new Error(`Audio upload failed (${putRes.status})`);
  }
  return publicUrl;
}

type DragMode = 'move' | 'start' | 'end';

/**
 * Attached reference-audio card: waveform preview with playback, and — when
 * the file is longer than the model's cap — a draggable/resizable selection
 * window for choosing which part to include.
 */
export function ReferenceAudioCard({
  value,
  maxSeconds,
  minSeconds = MIN_WINDOW_SEC,
  hint,
  onChange,
  onRemove,
  disabled,
}: {
  value: ReferenceAudioValue;
  maxSeconds: number;
  /** Shortest selectable window — voice cloning needs at least 10s. */
  minSeconds?: number;
  /** Overrides the default "which part guides the sound" caption. */
  hint?: string;
  onChange: (next: ReferenceAudioValue) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const { t } = useLocale('ai-images');
  const waveRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const dragRef = useRef<{
    mode: DragMode;
    pointerId: number;
    grabOffsetSec: number;
  } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadSec, setPlayheadSec] = useState<number | null>(null);

  const needsTrim = value.durationSec > maxSeconds + 0.05;
  const windowLength = value.endSec - value.startSec;

  // Peaks for the waveform — a fixed bucket count keeps drawing cheap and
  // resolution-independent (the canvas scales buckets to its width).
  const peaks = useMemo(() => {
    const BUCKETS = 320;
    const data = value.buffer.getChannelData(0);
    const bucketSize = Math.max(1, Math.floor(data.length / BUCKETS));
    const out: number[] = [];
    for (let i = 0; i < BUCKETS; i++) {
      let max = 0;
      const start = i * bucketSize;
      const end = Math.min(data.length, start + bucketSize);
      // Sample within the bucket (every 16th value) — exact max isn't needed
      // for a visual preview and full scans are slow on long files.
      for (let j = start; j < end; j += 16) {
        const v = Math.abs(data[j]);
        if (v > max) max = v;
      }
      out.push(max);
    }
    const overallMax = Math.max(0.01, ...out);
    return out.map((v) => v / overallMax);
  }, [value.buffer]);

  // Draw the waveform. Selection highlighting is done with overlay divs so we
  // don't redraw the canvas during drags.
  const drawWave = useCallback(() => {
    const canvas = canvasRef.current;
    const container = waveRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    const height = 56;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);
    const isDark = document.documentElement.classList.contains('dark');
    ctx.fillStyle = isDark ? '#64748b' : '#9ca3af';
    const barCount = peaks.length;
    const step = width / barCount;
    const barWidth = Math.max(1, step * 0.6);
    for (let i = 0; i < barCount; i++) {
      const h = Math.max(2, peaks[i] * (height - 8));
      ctx.fillRect(i * step, (height - h) / 2, barWidth, h);
    }
  }, [peaks]);

  useEffect(() => {
    drawWave();
    const container = waveRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => drawWave());
    observer.observe(container);
    return () => observer.disconnect();
  }, [drawWave]);

  // Playback preview: plays only the selected window.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      if (audio.currentTime >= value.endSec - 0.02) {
        audio.pause();
        audio.currentTime = value.startSec;
        setIsPlaying(false);
        setPlayheadSec(null);
      } else {
        setPlayheadSec(audio.currentTime);
      }
    };
    const onEnded = () => {
      setIsPlaying(false);
      setPlayheadSec(null);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, [value.startSec, value.endSec]);

  // Stop playback when the window changes so the preview always starts from
  // the new selection.
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
      setPlayheadSec(null);
    }
  }, [value.startSec, value.endSec]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      setPlayheadSec(null);
    } else {
      audio.currentTime = value.startSec;
      void audio.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  };

  const secFromClientX = (clientX: number): number => {
    const container = waveRef.current;
    if (!container) return 0;
    const rect = container.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * value.durationSec;
  };

  const applyWindow = (startSec: number, endSec: number) => {
    onChange({ ...value, startSec, endSec });
  };

  const onPointerDown = (mode: DragMode) => (e: React.PointerEvent) => {
    if (disabled || !needsTrim) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      pointerId: e.pointerId,
      grabOffsetSec: secFromClientX(e.clientX) - value.startSec,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const sec = secFromClientX(e.clientX);
    if (drag.mode === 'move') {
      const length = value.endSec - value.startSec;
      let start = sec - drag.grabOffsetSec;
      start = Math.min(Math.max(0, start), value.durationSec - length);
      applyWindow(start, start + length);
    } else if (drag.mode === 'start') {
      const start = Math.min(Math.max(0, sec), value.endSec - minSeconds);
      applyWindow(Math.max(start, value.endSec - maxSeconds), value.endSec);
    } else {
      const end = Math.max(
        Math.min(value.durationSec, sec),
        value.startSec + minSeconds,
      );
      applyWindow(value.startSec, Math.min(end, value.startSec + maxSeconds));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  const startPct = (value.startSec / value.durationSec) * 100;
  const endPct = (value.endSec / value.durationSec) * 100;
  const playheadPct =
    playheadSec !== null ? (playheadSec / value.durationSec) * 100 : null;

  return (
    <div className='mt-3 rounded-2xl border-2 border-primary-500 bg-primary-50/40 p-3 dark:bg-primary-900/20'>
      <audio ref={audioRef} src={value.objectUrl} preload='auto' />
      <div className='flex items-center gap-2.5'>
        <button
          type='button'
          onClick={togglePlay}
          disabled={disabled}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white transition-colors hover:bg-primary-600 disabled:opacity-50'
        >
          {isPlaying ? (
            <PauseIcon className='h-4 w-4' />
          ) : (
            <PlayIcon className='ml-0.5 h-4 w-4' />
          )}
        </button>
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-medium text-gray-700 dark:text-slate-200'>
            {value.file.name}
          </p>
          <p className='text-xs text-gray-500 dark:text-slate-400'>
            {needsTrim
              ? `${formatTime(value.startSec)} – ${formatTime(
                  value.endSec,
                )} · ${windowLength.toFixed(1)}s ${t('selected')}`
              : `${formatTime(value.durationSec)}`}
          </p>
        </div>
        <button
          type='button'
          onClick={onRemove}
          disabled={disabled}
          aria-label='Remove audio'
          className='rounded-full bg-black/40 p-1.5 text-white transition-colors hover:bg-black/60 disabled:opacity-50'
        >
          <XIcon className='h-3.5 w-3.5' />
        </button>
      </div>

      <div
        ref={waveRef}
        className={clsxm(
          'relative mt-2.5 h-14 select-none overflow-hidden rounded-lg bg-white dark:bg-slate-800',
          needsTrim && !disabled && 'cursor-grab',
        )}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <canvas ref={canvasRef} className='absolute inset-0' />
        {needsTrim && (
          <>
            {/* Dim the unselected regions */}
            <div
              className='absolute inset-y-0 left-0 bg-white/70 dark:bg-slate-900/70'
              style={{ width: `${startPct}%` }}
            />
            <div
              className='absolute inset-y-0 right-0 bg-white/70 dark:bg-slate-900/70'
              style={{ width: `${100 - endPct}%` }}
            />
            {/* Selection window */}
            <div
              className='absolute inset-y-0 rounded-md border-2 border-primary-500 bg-primary-500/10'
              style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
              onPointerDown={onPointerDown('move')}
            >
              <div
                className='absolute -left-0.5 top-0 flex h-full w-3 cursor-ew-resize items-center'
                onPointerDown={onPointerDown('start')}
              >
                <span className='ml-0.5 h-6 w-1 rounded-full bg-primary-500' />
              </div>
              <div
                className='absolute -right-0.5 top-0 flex h-full w-3 cursor-ew-resize items-center justify-end'
                onPointerDown={onPointerDown('end')}
              >
                <span className='mr-0.5 h-6 w-1 rounded-full bg-primary-500' />
              </div>
            </div>
          </>
        )}
        {playheadPct !== null && (
          <div
            className='pointer-events-none absolute inset-y-0 w-px bg-primary-600 dark:bg-primary-400'
            style={{ left: `${playheadPct}%` }}
          />
        )}
      </div>

      {needsTrim && (
        <p className='mt-2 text-xs text-gray-500 dark:text-slate-400'>
          {hint ??
            `${t('Drag to choose which part guides the sound')} (${t(
              'max',
            )} ${maxSeconds}s)`}
        </p>
      )}
    </div>
  );
}

export { ACCEPTED_AUDIO, MAX_AUDIO_FILE_BYTES };

/** Validate a picked file before decoding; returns an error message or null. */
export function validateReferenceAudioFile(file: File): string | null {
  const isAudioType = file.type.startsWith('audio/');
  const hasAudioExt = /\.(mp3|wav|m4a|aac|ogg|oga|flac|webm)$/i.test(file.name);
  if (!isAudioType && !hasAudioExt) {
    return 'Please choose an audio file (MP3, WAV, M4A, AAC, OGG, or FLAC).';
  }
  if (file.size > MAX_AUDIO_FILE_BYTES) {
    return 'Audio file is too large (max 50MB).';
  }
  return null;
}
